import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { apiUrl, socketUrl } from "../helper";
import { STANDS } from "../stands";
import "../styles/StandScreen.scss";

const StandScreen = () => {
    const { stand } = useParams();
    const [tickets, setTickets] = useState([]);
    const label = STANDS[stand]?.label || stand;

    useEffect(() => {
        if (!STANDS[stand]) return undefined;
        let active = true;
        axios.get(`${apiUrl}/api/stand/${stand}/recent`)
            .then((response) => {
                if (active) setTickets(response.data.tickets || []);
            })
            .catch(() => {
                if (active) setTickets([]);
            });
        return () => { active = false; };
    }, [stand]);

    const { sendMessage, readyState } = useWebSocket(socketUrl, {
        onMessage: (event) => {
            const data = JSON.parse(event.data);
            if (data.action === "stand-history" && data.stand === stand) {
                setTickets(data.tickets || []);
            }
            if (data.action === "stand-ticket" && data.ticket?.stand === stand) {
                setTickets((current) => [data.ticket, ...current].slice(0, 40));
            }
            if (data.action === "stand-dismiss" && data.stand === stand && data.ticketId) {
                setTickets((current) => current.filter((t) => t.id !== data.ticketId));
            }
        },
        shouldReconnect: () => true,
    });

    useEffect(() => {
        if (!STANDS[stand] || readyState !== ReadyState.OPEN) return;
        sendMessage(JSON.stringify({ action: "stand", stand }));
    }, [stand, readyState, sendMessage]);

    const dismissTicket = (ticketId) => {
        if (!ticketId) return;
        setTickets((current) => current.filter((t) => t.id !== ticketId));
        if (readyState === ReadyState.OPEN) {
            sendMessage(JSON.stringify({
                action: "stand-dismiss",
                stand,
                ticketId,
            }));
        }
    };

    if (!STANDS[stand]) {
        return <div className="stand-screen">Stand inconnu</div>;
    }

    return (
        <div className="stand-screen">
            <header className="stand-screen__header">
                <h1>{label}</h1>
                <span className="stand-screen__live">LIVE</span>
            </header>
            <p className="stand-screen__hint">Appuyer sur un ticket pour le retirer</p>
            <div className="stand-screen__list">
                {tickets.length === 0 ? (
                    <p className="stand-screen__empty">En attente de commandes…</p>
                ) : (
                    tickets.map((ticket) => (
                        <article
                            key={ticket.id}
                            className="stand-ticket"
                            role="button"
                            tabIndex={0}
                            onClick={() => dismissTicket(ticket.id)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    dismissTicket(ticket.id);
                                }
                            }}
                        >
                            <div className="stand-ticket__meta">
                                <strong>#{ticket.invoiceId}</strong>
                                <span>
                                    {ticket.createdAt
                                        ? new Date(ticket.createdAt).toLocaleTimeString("fr-FR", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            second: "2-digit",
                                        })
                                        : ""}
                                </span>
                                <span>Caisse {ticket.code}</span>
                                <span className="stand-ticket__dismiss">Retirer</span>
                            </div>
                            <ul>
                                {(ticket.items || []).map((item, index) => (
                                    <li key={`${ticket.id}-${item.id || index}`}>
                                        <span className="qty">{item.amount}×</span>
                                        <span className="name">{item.name}</span>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    ))
                )}
            </div>
        </div>
    );
};

export default StandScreen;
